'use client';

import React, { JSX, useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';

import { useRegister } from '@/api/auth/hooks/useRegister';
import Checkbox from '@/shared/components/Checkbox/Checkbox';
import { EyeClose } from '@/shared/components/Icons/EyeIcon/EyeClose';
import { EyeOpen } from '@/shared/components/Icons/EyeIcon/EyeOpen';

interface SignUpFormData {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    confirmPassword: string;
    accessCode: string;
    terms: boolean;
}

export default function SignInForm(): JSX.Element {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { mutate: registerUser, isPending } = useRegister();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<SignUpFormData>({
        mode: 'onBlur',
    });

    const password = watch('password');

    const onSubmit = (data: SignUpFormData): void => {
        registerUser({
            email: data.email,
            password_1: data.password,
            password_2: data.confirmPassword,
            first_name: data.firstName,
            last_name: data.lastName,
            security_code: data.accessCode,
        });
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="max-w-[560px] w-full mx-auto bg-white flex flex-col flex-1 md:flex-none gap-6"
        >
            <div className="space-y-6">
                <h2 className="text-[32px] font-bold">Sign Up</h2>

                <div className="flex flex-col">
                    <label className="mb-1 font-medium">Email</label>
                    <input
                        placeholder="Enter your Email"
                        type="email"
                        {...register('email', {
                            required: 'Email is required',
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Invalid email address',
                            },
                        })}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.email && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.email.message}
                        </p>
                    )}
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 font-medium">First Name</label>
                    <input
                        placeholder="Enter your first name"
                        type="text"
                        {...register('firstName', {
                            required: 'First name is required',
                            minLength: {
                                value: 2,
                                message:
                                    'First name must be at least 2 characters',
                            },
                            maxLength: {
                                value: 50,
                                message:
                                    'First name must not exceed 50 characters',
                            },
                            pattern: {
                                value: /^[A-Za-z'\s-]+$/,
                                message:
                                    'First name can only contain Latin letters, spaces, hyphens and apostrophes',
                            },
                        })}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.firstName && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.firstName.message}
                        </p>
                    )}
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 font-medium">Last Name</label>
                    <input
                        placeholder="Enter your last name"
                        type="text"
                        {...register('lastName', {
                            required: 'Last name is required',
                            minLength: {
                                value: 2,
                                message:
                                    'Last name must be at least 2 characters',
                            },
                            maxLength: {
                                value: 50,
                                message:
                                    'Last name must not exceed 50 characters',
                            },
                            pattern: {
                                value: /^[A-Za-z'\s-]+$/,
                                message:
                                    'Last name can only contain Latin letters, spaces, hyphens and apostrophes',
                            },
                        })}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.lastName && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.lastName.message}
                        </p>
                    )}
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 font-medium">Password</label>
                    <div className="relative">
                        <input
                            placeholder="Enter your password"
                            type={showPassword ? 'text' : 'password'}
                            {...register('password', {
                                required: 'Password is required',
                                minLength: {
                                    value: 8,
                                    message:
                                        'Password must be at least 8 characters',
                                },
                                maxLength: {
                                    value: 64,
                                    message:
                                        'Password must not exceed 64 characters',
                                },
                                pattern: {
                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%&*_-]{8,64}$/,
                                    message:
                                        'Password must be 8-64 characters long and contain at least one uppercase letter, one lowercase letter, one number, and may include special characters (!@#$%&*_-)',
                                },
                            })}
                            className="w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showPassword ? (
                                <EyeClose className="w-5 h-5" />
                            ) : (
                                <EyeOpen className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.password.message}
                        </p>
                    )}
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 font-medium">Confirm Password</label>
                    <div className="relative">
                        <input
                            placeholder="Confirm your password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            {...register('confirmPassword', {
                                required: 'Please confirm your password',
                                validate: (value) =>
                                    value === password ||
                                    'Passwords do not match',
                            })}
                            className="w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showConfirmPassword ? (
                                <EyeClose className="w-5 h-5" />
                            ) : (
                                <EyeOpen className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.confirmPassword.message}
                        </p>
                    )}
                </div>

                <div className="flex flex-col">
                    <label className="mb-1 font-medium">Access Code</label>
                    <input
                        placeholder="Enter access code"
                        type="text"
                        {...register('accessCode', {
                            required: 'Access code is required',
                        })}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.accessCode && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.accessCode.message}
                        </p>
                    )}
                </div>

                <div className="flex items-start space-x-3">
                    <Checkbox
                        label={
                            <>
                                I agree to the{' '}
                                <a
                                    href="/privacy"
                                    className="text-[#C32033] font-semibold"
                                >
                                    Terms of Service
                                </a>
                                .
                            </>
                        }
                        registration={register('terms', {
                            required: 'You must accept the terms of use',
                        })}
                        error={errors.terms}
                    />
                </div>
            </div>

            <div className="space-y-6 mt-auto md:mt-0">
                <button
                    disabled={isPending}
                    type="submit"
                    className="w-full bg-[#C32033] text-white py-2 rounded-[10px] font-medium cursor-pointer"
                >
                    {isPending ? 'Processing...' : 'Sign Up'}
                </button>

                <p className="text-center">
                    Already have an account?{' '}
                    <Link href="/sign-in" className="text-[#C32033]">
                        Sign In
                    </Link>
                </p>
            </div>
        </form>
    );
}
